import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { PRIMARY_COLOR } from '../../constants'
import { calculateProductPrice, deepCopy, moveElement } from '../../helpers'
import { Product } from '../../redux-toolkits/products/products.type'
import { EmptyState } from '../emptyState/emptyState'
import Icon from '../icon'
import { LoadingOval } from '../spinner/Spinner'

interface Iprops {
  product: Product
  onViewProduct?: (product: Product) => void
  disableClick?: boolean
  selected?: boolean
  showDelete?: boolean
  onDelete?: () => void
  sortable?: boolean
  id?: string
  className?: string
}
export const ProductTile: FC<Iprops> = ({
  product,
  onViewProduct = () => {},
  disableClick = false,
  //selected = false,
  showDelete = false,
  onDelete = () => {},
  sortable = false,
  id = '',
  className = '',
}) => {
  const outOfStock = product.isVariant
    ? product?.variantCombinations?.reduce(
        (sum, item) => sum + Number(item.inventoryCount),
        0,
      ) === 0
    : product?.inStockQuantity === 0

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const navigate = useNavigate()

  const viewProduct = () => {
    if (!disableClick) {
      onViewProduct
        ? onViewProduct(product)
        : navigate(`/products/view/${product?._id}`)
    }
  }

  return (
    <div
      className={`relative md:pr-6 border-b border-default-gray-3 pb-5 flex justify-between gap-4 items-center last:border-b-0 cursor-pointer bg-frost ${className}`}
      ref={setNodeRef}
      style={style}
      onClick={() => {
        viewProduct()
      }}
    >
      {sortable && (
        <button
          className="w-fit cursor-grab !max-w-[50px]"
          {...attributes}
          {...listeners}
          type="button"
        >
          <Icon id="rearrange" width={24} height={24} />
        </button>
      )}

      <div
        className={clsx(
          `relative w-[88px] md:w-[140px] h-[88px] md:h-[140px]`,
          {
            'opacity-25': outOfStock,
          },
        )}
      >
        <img
          src={product?.images[0]}
          alt={product?.name}
          className="rounded-md w-full h-full object-cover"
        />
      </div>

      <div className="flex-1 flex flex-col gap-1">
        <div className="flex gap-4 items-center justify-between">
          <p
            className={clsx(
              `
                text-base text-left
                justify-between
                font-medium
                `,
            )}
          >
            {product?.name}
          </p>

          {showDelete && (
            <button
              className={clsx('p-2 bg-danger/10 rounded-full w-fit')}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            >
              <Icon className="text-danger" width={16} height={16} id="bin" />
            </button>
          )}
        </div>

        <p
          className={clsx(
            `
                line-clamp-2 overflow-hidden 
                text-sm text-ellipsis
                text-sec-black
                `,
          )}
        >
          {product?.description}
        </p>

        <div className={clsx(`text-sm text-black`)}>
          {product?.isCombo && 'Starting from'} {calculateProductPrice(product)}
        </div>
      </div>
    </div>
  )
}

export const ProductTileContainer = ({
  products,
  handleSortEnd = () => {},
  onDeleteProduct = () => {},
  canDelete = false,
  onViewProduct,
  sortable = false,
  loading,
}: {
  products: Product[]
  handleSortEnd?: (p: Product[]) => void
  onDeleteProduct?: (p: Product, idx: number) => void
  canDelete?: boolean
  onViewProduct?: (product: Product) => void
  sortable?: boolean
  loading?: boolean
}) => {
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      const dataCopy: Product[] = deepCopy(products)
      const dataCopyMod = dataCopy.map((p) => {
        return {
          id: `${p.name}-${p._id}`,
          ...p,
        }
      })

      const oldIndex = dataCopyMod.findIndex((item) => item.id === active.id)
      const newIndex = dataCopyMod.findIndex((item) => item.id === over.id)

      handleSortEnd(moveElement(dataCopy, oldIndex, newIndex))
    }
  }
  return loading ? (
    <div className="w-full flex items-center justify-center">
      <LoadingOval color={PRIMARY_COLOR} />
    </div>
  ) : (
    <DndContext
      onDragEnd={handleDragEnd}
      // modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={
          products.map((product) => {
            return {
              id: `${product?.name}-${product?._id}`,
              ...product,
            }
          }) ?? []
        }
      >
        {products.length > 0 ? (
          <div className="grid frid-cols-1 md:grid-cols-2 gap-y-6">
            {products.map((p, idx) => {
              return (
                <ProductTile
                  key={`${p._id}-${idx}`}
                  product={p}
                  onViewProduct={onViewProduct}
                  disableClick={false}
                  selected={false}
                  sortable={sortable}
                  showDelete={canDelete}
                  onDelete={() => {
                    onDeleteProduct(p, idx)
                  }}
                  id={`${p.name}-${p._id}`}
                  className={
                    products.length % 2 === 0
                      ? '[&:nth-last-child(2)]:border-b-0 [&:nth-last-child(2)]:!pb-0'
                      : ''
                  }
                />
              )
            })}
          </div>
        ) : (
          <EmptyState />
        )}
      </SortableContext>
    </DndContext>
  )
}
